import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PickemQuestion } from '@low6-pickem/api-types';
import { questionComponentFactory } from '@/factories/questionComponentFactory';
import type { QuestionAdapterContext } from '@/factories/questionComponentFactory.types';
import { useLocalizedFieldGetter } from '@/utils/localized-field';
import { setLowResImages } from '@/utils/lowResImages';
import { setLeagueIp, setTeamAssetFolder } from '@/utils/leagueIp';
// Import registry to trigger component registration
import '@/factories/questionComponentRegistry';

interface QuestionRendererProps {
  question: PickemQuestion;
  questionNumber?: number; // Question number for display (1-6), undefined for tiebreaker
  selectedTeams: Record<string, string>;
  selectedProps: Record<string, 'yes' | 'no'>;
  selectedOverUnder: Record<string, 'over' | 'under'>;
  tieBreaker: string;
  onTeamSelect: (questionId: string, teamId: string | undefined) => void;
  onPropSelect: (questionId: string, answer: 'yes' | 'no') => void;
  onOverUnderSelect: (questionId: string, answer: 'over' | 'under' | undefined) => void;
  onTieBreakerChange: (value: string) => void;
  isViewOnly: boolean;
  selectionResults?: Record<string, boolean>;
  selectionPoints?: Record<string, number>;
  /** When absent/false, pick-percentage badges on option cards are hidden. */
  showPopularPicks?: boolean;
  /**
   * When true, team/jersey artwork is served from the low-res `-low` blob
   * folders. Driven by `pickem.style_config.lowResImages` (DKPLAT-669).
   */
  lowResImages?: boolean;
  /**
   * Whether this contest may use licensed (IP) team artwork. Driven by
   * `pickem.league_ip` (DKPLAT-357). With it false, NFL renders the unlicensed
   * team jerseys instead of the paid-for logos.
   *
   * Defaults to false — absent means "no IP rights", which fails safe: the worst
   * case is showing a jersey where a logo was allowed, not licensed artwork on a
   * contest that has no right to it.
   */
  leagueIp?: boolean;
  /**
   * Contest picks-core team image folder from `style_config.teamAssetFolder`.
   * When set, remaps team marks into that folder (overrides binary League IP default).
   */
  teamAssetFolder?: string | null;
}

export const QuestionRenderer: React.FC<QuestionRendererProps> = ({
  question,
  questionNumber,
  selectedTeams,
  selectedProps,
  selectedOverUnder,
  tieBreaker,
  onTeamSelect,
  onPropSelect,
  onOverUnderSelect,
  onTieBreakerChange,
  isViewOnly,
  selectionResults,
  selectionPoints,
  showPopularPicks,
  lowResImages,
  leagueIp,
  teamAssetFolder,
}) => {
  const { t } = useTranslation();
  const getField = useLocalizedFieldGetter();

  const config = questionComponentFactory.get(question.question_type);

  if (!config) {
    console.warn(`[QuestionRenderer] Unknown question type: ${question.question_type}`);
    return null;
  }

  // Build context for adapter
  const context: QuestionAdapterContext = {
    question,
    questionNumber,
    selections: {
      teams: selectedTeams,
      props: selectedProps,
      overUnder: selectedOverUnder,
      tieBreaker,
    },
    handlers: {
      onTeamSelect,
      onPropSelect,
      onOverUnderSelect,
      onTieBreakerChange,
    },
    state: {
      isViewOnly,
      selectionResults,
      selectionPoints,
      showPopularPicks,
    },
    hooks: {
      t,
      getField,
    },
  };

  // Get component and props from factory.
  // The low-res and league-IP flags are ambient for the synchronous adapter call
  // only: adapters are pure sync transforms that build every team/jersey URL via
  // the image chokepoints, so setting them here (and resetting immediately)
  // scopes them precisely to this question's image URLs without leaking to other
  // screens. Both are reset even though the adapter cannot throw usefully — a
  // leaked IP flag would render licensed artwork on the next contest.
  const Component = config.component;
  setLowResImages(lowResImages === true);
  setLeagueIp(leagueIp === true);
  setTeamAssetFolder(teamAssetFolder ?? null);
  const props = config.adapter(context);
  setLowResImages(false);
  setLeagueIp(false);
  setTeamAssetFolder(null);

  return <Component {...props} />;
};
